import React, { useState, useRef } from "react";
import styles from "./CostCalculatorModal.module.css";
import NewModal from "../NewModal/NewModal";
import AnimatedButton2 from "../AnimatedButton/AnimatedButton2";
import { PAYG_PRICING, FREE_MINUTES } from "../../lib/paygPricing";

// PAYG economics. The monthly fee and per-minute rate come from the `pricing`
// prop (USD or CAD — see lib/paygPricing.js), set inside the component.
const FREE = FREE_MINUTES; // free MINUTES in the first month
const MIN_PER_CALL = 2.5; // avg minutes per real call (50 free min ≈ 20 calls)
const CALLS_MIN = 20;     // slider floor (also the free-call allowance)
const CALLS_MAX = 600;    // slider ceiling
const FREE_CALLS = Math.round(FREE / MIN_PER_CALL); // 20

// Plans a heavy user should switch to, checked HIGH → LOW so we recommend the
// richest plan the projected spend has outgrown. `perMin` is the point where
// PAYG stops being cheaper (price − base) / rate — shown in the card copy.
const PLANS = [
  { key: "growth", name: "GROWTH", price: 599, minutes: 1500, tone: "growth", tagline: "Perfect for a high-volume business", icon: "/svg/growth-icon_icon.svg" },
  { key: "scaler", name: "SCALER", price: 299, minutes: 750, tone: "scaler", tagline: "Perfect for a growing business", icon: "/svg/scaler-icon_new.svg" },
  { key: "starter", name: "STARTER", price: 119, minutes: 300, tone: "starter", tagline: "Perfect for a startup or micro business", icon: "/svg/starter-icon_new.svg" },
];

// "24 Aug" style label, `offset` months from today. new Date() is fine at
// browser runtime (this isn't a sandboxed script).
const monthLabel = (offset) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset);
  return d.toLocaleDateString("en-US", { day: "numeric", month: "short" });
};

// Small check icon (inherits color from the pill it's in).
const check = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill="currentColor" />
    <path d="M8 12l2.5 2.5L16 9" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CostCalculatorModal = ({ isOpen, onClose, agentName = "Sophia", onSubscribe, pricing = PAYG_PRICING.usd }) => {
  // Fee, per-minute rate and symbol for the visitor's currency (USD / CAD).
  const RATE = pricing.rate;
  const BASE = pricing.fee;
  const money = (n) => `${pricing.symbol}${Math.round(n * 100) / 100}`;

  // The slider is in CALLS; minutes (what we bill on) are derived from it.
  const [calls, setCalls] = useState(CALLS_MIN);
  // Card starts collapsed — only the "Better Option" header (name + price) is
  // visible; user taps the chevron to expand the body.
  const [collapsed, setCollapsed] = useState(true);

  // Swipe-to-toggle: swipe up collapses, swipe down expands. `swipedRef`
  // suppresses the click that a touch also fires, so a swipe doesn't get
  // undone by the card's tap-to-toggle handler.
  const touchStartYRef = useRef(null);
  const swipedRef = useRef(false);
  const SWIPE = 40; // px threshold before it counts as a swipe

  const handleTouchStart = (e) => {
    touchStartYRef.current = e.touches[0].clientY;
    swipedRef.current = false;
  };
  const handleTouchMove = (e) => {
    if (touchStartYRef.current == null) return;
    const dy = e.touches[0].clientY - touchStartYRef.current;
    if (Math.abs(dy) > SWIPE) {
      setCollapsed(dy < 0); // up (dy<0) → collapse; down → expand
      swipedRef.current = true;
      touchStartYRef.current = null; // lock until the next touchstart
    }
  };
  const handleCardClick = () => {
    if (swipedRef.current) {
      swipedRef.current = false; // this "click" was really a swipe — ignore it
      return;
    }
    setCollapsed((c) => !c);
  };

  const minutes = Math.round(calls * MIN_PER_CALL);              // billable minutes
  const usage = minutes * RATE;
  const firstMonth = BASE + Math.max(0, minutes - FREE) * RATE; // first month: 50 free
  const steady = BASE + usage;                                   // steady month
  const pct = Math.min(100, ((calls - CALLS_MIN) / (CALLS_MAX - CALLS_MIN)) * 100);

  // Shrink the step price to 22px once its number part is longer than 2 chars
  // (e.g. "124.8") so big values don't overflow the small step card.
  const priceCls = (label) =>
    String(label).replace(/[^0-9.]/g, "").length > 2 ? styles.stepPriceSm : "";

  // Recommend the richest plan whose flat price the projected spend has passed.
  const recommended = PLANS.find((p) => steady >= p.price) || null;

  // Plan recommendation floats ABOVE the sheet (outside the modal body) and
  // slides down from the top. When it's showing, the modal's × moves inside
  // the sheet's top-right corner so the two don't collide.
  const planCard = recommended ? (
    <div
      className={`${styles.planCard} ${styles[`ring_${recommended.tone}`]} ${collapsed ? styles.planCardCollapsed : ""}`}
      key={recommended.key}
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setCollapsed((c) => !c);
        }
      }}
    >
      <img className={styles.betterBadge} src="/svg/better_option.svg" alt="Better Option" />

      {/* Header: tagline + plan name (left), price (right) */}
      <div className={styles.planHead}>
        <div className={styles.planHeadLeft}>
          <p className={styles.planTagline}>{recommended.tagline}</p>
          <h3 className={styles.planName}>{recommended.name}</h3>
        </div>
        <div className={styles.planPriceTag}>
          <span className={styles.planDollar}>$</span>{recommended.price}
          <span className={styles.perMonth}>per month</span>
        </div>
      </div>

      {/* Collapsible body — animates height/opacity for a smooth toggle. */}
      <div className={`${styles.planBody} ${collapsed ? styles.planBodyCollapsed : ""}`}>
        <div className={styles.planMid}>
          {/* Left: minutes + plan icon */}
          <div className={styles.planMidLeft}>
            <div className={styles.planMinBlock}>
              <span className={styles.planBar} />
              <div className={styles.planMinText}>
                <span className={styles.planMinNum}>{recommended.minutes}</span>
                <span className={styles.planMinUnit}>minutes</span>
              </div>
            </div>
            <img className={styles.planIcon} src={recommended.icon} alt="" />
          </div>

          {/* Right: feature pills */}
          <ul className={styles.planFeatures}>
            <li>no per-minute charges</li>
            <li>no usage surprises</li>
            <li>Lower cost after ~{Math.round((recommended.price - BASE) / RATE)} min/month</li>
          </ul>
        </div>

        {/* Wrapper stops the card's toggle handler from firing on Subscribe. */}
        <div onClick={(e) => e.stopPropagation()}>
          <AnimatedButton2
            label={
              <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                Subscribe and Save <span aria-hidden="true">→</span>
              </span>
            }
            variant="purple"
            onClick={() => onSubscribe?.(recommended)}
            positionType="relative"
            bottom="0px"
          />
        </div>
      </div>

      {/* Collapse / expand the card. */}
      <button
        type="button"
        className={styles.collapseToggle}
        onClick={(e) => {
          e.stopPropagation();
          setCollapsed((c) => !c);
        }}
        aria-label={collapsed ? "Expand" : "Collapse"}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          className={collapsed ? styles.chevDown : styles.chevUp}
        >
          <path d="M6 9l6 6 6-6" stroke="#6524EB" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  ) : null;

  return (
    <NewModal
      isOpen={isOpen}
      onClose={onClose}
      title={null}
      height="auto"
      bodyMaxHeight="92vh"
      closeBtnPosition="inside"
      topContent={planCard}
    >
      <div className={styles.calc}>
        <h2 className={styles.calcTitle}>What will {agentName} cost you?</h2>
        <p className={styles.calcSub}>
          You only pay for real calls it answers. Spam is blocked free — never billed.
        </p>

        {/* Expense label */}
        <div className={styles.expenseLabel}>
          <span className={styles.expenseLine} />
          <span className={styles.expenseText}>YOUR MONTH TO MONTH EXPENSE</span>
          <span className={styles.expenseLine} />
        </div>

        {/* 3-step timeline */}
        <div className={styles.steps}>
          <div className={`${styles.stepCard} ${styles.stepGreen}`}>
            <span className={styles.stepBadge}>
              <span className={styles.stepNum}>1</span>Current
            </span>
            <p className={styles.stepWhen}>Today<span>Start with</span></p>
            <p className={`${styles.stepPrice} ${priceCls(money(BASE))}`}>{money(BASE)}</p>
            <span className={styles.stepPill}>{check} Including 50 free minutes</span>
          </div>

          <span className={styles.stepArrow} aria-hidden="true">›</span>

          <div className={`${styles.stepCard} ${styles.stepBlue}`}>
            <span className={styles.stepBadge}>
              <span className={styles.stepNum}>2</span>Next
            </span>
            <p className={styles.stepWhen}>{monthLabel(1)}<span>next month</span></p>
            <p className={`${styles.stepPrice} ${priceCls(money(firstMonth))}`}>{money(firstMonth)}</p>
            <span className={styles.stepPill}>
              {check}{" "}
              {minutes > FREE
                ? `${money(BASE)} + ${minutes - FREE} mins @ ${money(RATE)}/min`
                : `${money(BASE)} activation fee only`}
            </span>
          </div>

          <span className={styles.stepArrow} aria-hidden="true">›</span>

          <div className={`${styles.stepCard} ${styles.stepPurple}`}>
            <span className={styles.stepBadge}>
              <span className={styles.stepNum}>3</span>After that
            </span>
            <p className={styles.stepWhen}>{monthLabel(2)}<span>onwards</span></p>
            <p className={`${styles.stepPrice} ${priceCls(money(steady))}`}>
              {money(steady)}<span className={styles.perM}>/m</span>
            </p>
            <span className={styles.stepPill}>{check} {money(BASE)} + {minutes} mins @ {money(RATE)}/min</span>
          </div>
        </div>

        <p className={styles.freeLine}>
          (Your first month includes <span> {FREE} free minutes. Enough for {FREE_CALLS} calls</span>)
        </p>

        {/* Slider */}
        <div className={styles.sliderBox}>
          <p className={styles.sliderLabel}>Number of calls you expect to receive, every month</p>

          <div className={styles.sliderMain}>
            <div className={styles.sliderLeft}>
              <div className={styles.scaleRow}>
                <span>{CALLS_MIN} calls</span>
                <span>{CALLS_MAX} calls</span>
              </div>
              <input
                type="range"
                min={CALLS_MIN}
                max={CALLS_MAX}
                step="1"
                value={calls}
                onChange={(e) => setCalls(Number(e.target.value))}
                className={styles.slider}
                style={{ "--pct": `${pct}%` }}
                aria-label="Expected monthly calls"
              />
              <p className={styles.sliderHint}>
                <img src="/svg/bulb_icon.svg" alt="bulb" />
                {minutes} minutes = {calls} real calls approx.
              </p>
            </div>

            {/* Selected-calls badge */}
            <div className={styles.selectedBox}>
              <span className={styles.selBig}>{calls}</span>
              <span className={styles.selLabel}>calls/mo</span>
            </div>
          </div>
        </div>
      </div>
    </NewModal>
  );
};

export default CostCalculatorModal;
